import React, { useState, useCallback, useEffect } from "react";
import MyFilterItem from "./MyFilterItem";
import { useSearchParams } from "react-router-dom";
import { getFilters } from "../../../assets/utils/Table.helpers";
import { DATE_RANGE_FILTER } from "../../../core/constant/filterComponent-const";

const TablePaginationFilter = ({ rows, onFilter, options }) => {
  const [searchParams] = useSearchParams();
  const [filters, setFilters] = useState({});

  // Met à jour les filtres à chaque changement de searchParams
  useEffect(() => {
    setFilters(getFilters(searchParams));
  }, [searchParams]);

  const isDataInDateFormat = useCallback((data) => {
    if (!data) return true;
    let date = new Date(data);
    return date instanceof Date || !data;
  }, []);

  const getDataFromAttribute = useCallback(
    (isObjectValue, filterKey, filterSubKey, isDate, isSelect, selectOptions) => {
      if (!rows) return undefined;

      // Si isSelect est true, utiliser directement les options prédéfinies
      if (isSelect && selectOptions && Array.isArray(selectOptions)) {
        return selectOptions.map(option => option.value);
      }

      // Extraction spécifique pour le filtre mois
      if (filterKey === "month") {
        return rows
          .map((item) => {
            const date = new Date(item.date);
            return date.getMonth() + 1;
          })
          .filter((v, i, a) => a.indexOf(v) === i);
      }

      // Extraction classique selon clé
      let data = Array.from(rows, (item) =>
        !isObjectValue ? item[filterKey] : item[filterKey][filterSubKey]
      );

      if (isDate && data.every(isDataInDateFormat)) {
        return data
          .filter((item) => item)
          .map((elt) => {
            const date = new Date(elt);
            return date?.getFullYear();
          });
      }
      return data;
    },
    [rows, isDataInDateFormat]
  );

  const transformDataIntoArray = useCallback(
    (data) => (Array.isArray(data) ? data : [data]),
    []
  );

  const deleteUndefinedValuesFromFilters = useCallback((allFilters) => {
    if (!allFilters) return allFilters;
    for (let key in allFilters) {
      if (allFilters[key] === undefined) {
        delete allFilters[key];
      }
    }
    return allFilters;
  }, []);

  const handleFilterChange = useCallback(
    (currentFilter, filterKey, dateRangeFilter) => {
      setFilters((prev) => {
        // Nouvelle copie des filtres, ajout du filtre modifié
        let newFilters = {};

        if (filterKey === DATE_RANGE_FILTER) {
          newFilters = deleteUndefinedValuesFromFilters({
            ...prev,
            [dateRangeFilter?.startDate]: currentFilter?.startDate,
            [dateRangeFilter?.endDate]: currentFilter?.endDate
          });
        } else {
          newFilters = deleteUndefinedValuesFromFilters({
            ...prev,
            [filterKey]: currentFilter
          });
        }

        // ** Clé importante : Si le filtre `date` est supprimé/vidé, on enlève aussi `month` **
        const dateFilterValue = newFilters.date;
        const isDateEmpty =
          dateFilterValue === undefined ||
          dateFilterValue === null ||
          (typeof dateFilterValue === "string" &&
            dateFilterValue.trim() === "") ||
          (typeof dateFilterValue === "object" &&
            Object.keys(dateFilterValue).length === 0); // empty object

        if (isDateEmpty && "month" in newFilters) {
          delete newFilters.month;
        }

        // Construire l'objet à envoyer au parent
        // Inclure explicitement les clés avec undefined pour qu'elles soient supprimées de l'URL
        const filterToSend = { ...newFilters };

        // Si la date a été supprimée, inclure undefined pour supprimer de l'URL
        if (filterKey === "date" && currentFilter === undefined) {
          filterToSend.date = undefined;
          filterToSend.month = undefined; // Supprimer aussi le mois
        }
        if (filterKey === "month" && currentFilter === undefined) {
          filterToSend.month = undefined;
        }

        // Pour les autres filtres non-date: si le filtre est effacé (undefined),
        // s'assurer d'inclure explicitement la clé avec undefined pour qu'elle
        // soit retirée des search params (sinon la valeur précédente pourrait
        // persister dans l'URL car on merge avec les params existants).
        if (filterKey !== DATE_RANGE_FILTER && currentFilter === undefined) {
          filterToSend[filterKey] = undefined;
        }

        // On remonte les filtres mis à jour
        onFilter(filterToSend);
        return newFilters;
      });
    },
    [onFilter, deleteUndefinedValuesFromFilters]
  );

  // Filtrer les options nulles ou invalides
  const validOptions = React.useMemo(() => {
    if (Array.isArray(options)) {
      return options.filter((item) => item && item.props);
    }
    return options && options.props ? [options] : [];
  }, [options]);

  // Grouper les options en chunks de 3 pour affichage en grille
  const chunks = React.useMemo(() => {
    return transformDataIntoArray(validOptions).reduce(
      (resultArray, item, index) => {
        const chunkIndex = Math.floor(index / 3);
        if (!resultArray[chunkIndex]) resultArray[chunkIndex] = [];
        resultArray[chunkIndex].push(item);
        return resultArray;
      },
      []
    );
  }, [validOptions, transformDataIntoArray]);

  return (
    <div>
      {Array.isArray(validOptions) && validOptions.length > 1 ? (
        <div style={{ display: "grid", rowGap: "1ch" }}>
          {chunks.map((chunk, index) => (
            <div
              key={index}
              style={{
                display: "grid",
                gap: "10px",
                gridTemplateColumns: "repeat(auto-fill, minmax(280px, 10fr))"
              }}>
              {chunk.map((item, idx) => (
                <div key={idx} className="w-100">
                  <MyFilterItem
                    name={item.props.children}
                    filterKey={item.props.filterKey}
                    data={
                      item.props.isDateRange
                        ? []
                        : getDataFromAttribute(
                            item.props.isObjectKey,
                            item.props.filterKey,
                            item.props.subKey,
                            item.props.isDate,
                            item.props.isSelect,
                            item.props.options
                          )
                    }
                    isMulti={item.props.isMulti}
                    onFilterUpdate={handleFilterChange}
                    itemDisplayValueFunc={item.props.itemDisplayValueFunc}
                    defaultValue={filters?.[item.props.filterKey]}
                    hasDefaultValue={filters?.[item.props.filterKey]}
                    isDateRange={item.props.isDateRange}
                    dateRangeKeyFilter={
                      item.props.isDateRange
                        ? {
                            startDate: item.props.rangeStartDate,
                            endDate: item.props.rangeEndDate
                          }
                        : undefined
                    }
                  />
                </div>
              ))}
            </div>
          ))}
        </div>
      ) : validOptions.length === 1 ? (
        <div className="w-50" style={{ rowGap: "10px" }}>
          <MyFilterItem
            name={validOptions[0].props.children}
            filterKey={validOptions[0].props.filterKey}
            data={
              validOptions[0].props.isDateRange
                ? []
                : getDataFromAttribute(
                    validOptions[0].props.isObjectKey,
                    validOptions[0].props.filterKey,
                    validOptions[0].props.subKey,
                    validOptions[0].props.isDate,
                    validOptions[0].props.isSelect,
                    validOptions[0].props.options
                  )
            }
            isMulti={validOptions[0].props.isMulti}
            onFilterUpdate={handleFilterChange}
            itemDisplayValueFunc={validOptions[0].props.itemDisplayValueFunc}
            defaultValue={filters?.[validOptions[0].props.filterKey]}
            hasDefaultValue={filters?.[validOptions[0].props.filterKey]}
            isDateRange={validOptions[0].props.isDateRange}
            dateRangeKeyFilter={
              validOptions[0].props.isDateRange
                ? {
                    startDate: validOptions[0].props.rangeStartDate,
                    endDate: validOptions[0].props.rangeEndDate
                  }
                : undefined
            }
          />
        </div>
      ) : null}
    </div>
  );
};

export default TablePaginationFilter;
